<template>
	<!-- .cog-toggle is deprecated, use .cog-switch instead -->
	<div class="cog-switch cog-toggle cog-input" :class="{'cog-switch--condensed cog-toggle--condensed': isCondensed}">
		<div v-if="readonly" class="is-read-only">{{ displayValue || "&nbsp;" }}</div>
		<template v-else>
			<template v-if="!chameleon">
				<el-switch :id="'cog-'+$parent.$source.id" ref="switch" v-model="internalValue" :name=" $parent.$source.label" :active-text="activeText" :inactive-text="inactiveText" @change="handleChange"> </el-switch>
				<div v-if="isCondensed" class="cog-switch__condensed-label cog-toggle__condensed-label cog-helptext" :class="{'is-active': internalValue}">{{ displayValue }}</div>
			</template>
			<div v-else class="cog-switch--native cog-toggle--native">
				<span class="cog-switch__left-label cog-toggle--left-label" @click="handleChange(false, $event)">{{ inactiveText }}</span>
				<input :id="'cog-'+$parent.$source.id" v-model="numericValue" type="range" min="0" max="1" @change="handleNativeChange" @keydown.prevent.space="handleChange(!internalValue, $event)" />
				<span class="cog-switch__right-label cog-toggle--right-label" @click="handleChange(true, $event)">{{ activeText }}</span>
			</div>
		</template>
	</div>
</template>

<script>
	import Vue from 'vue';
	import { Switch } from '@cognitoforms/element-ui';
	import { uiDebounce } from 'src/util/debounce';
	Vue.use(Switch);

	export default {
		name: 'CSwitch',
		model: {
			prop: 'checked',
			event: 'change'
		},
		props: {
			checked: Boolean,
			chameleon: Boolean,
			readonly: Boolean,
			activeText: { type: String, default: '' },
			inactiveText: { type: String, default: '' }
		},
		data: function () {
			return {
				internalValue: this.checked,
				numericValue: this.checked ? '1' : '0',
				width: null,
				isCondensed: false,
				debouncedResize: uiDebounce(() => this.resize())
			};
		},
		computed: {
			displayValue() {
				return this.internalValue ? this.activeText : this.inactiveText;
			}
		},
		watch: {
			checked: function (valueProp) {
				this.internalValue = valueProp;
				this.numericValue = valueProp ? '1' : '0';
			}
		},
		mounted() {
			this.resize();
			window.addEventListener('resize', this.debouncedResize);
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.debouncedResize);
		},
		methods: {
			handleChange(value, ev) {
				this.internalValue = value;
				this.$emit('change', value, ev);
			},
			handleNativeChange(ev) {
				this.handleChange(ev.target.value === '1', ev);
			},
			resize() {
				if (!this.$refs.switch)
					return;

				const parentWidth = this.$parent.$el.clientWidth - parseInt(getComputedStyle(this.$parent.$el).paddingLeft) * 2;
				if (parentWidth === this.width)
					return;

				this.isCondensed = false;
				this.$nextTick(() => {
					this.isCondensed = this.$refs.switch.$el.clientWidth > parentWidth;
					this.width = parentWidth;
				});
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-switch {
		$switch--width: 1.5em;
		$switch--padding: .3em;
		$switch--core-width: $switch--width * 2 + $switch--padding * 2;

		.el-switch {
			display: inline-flex;
			align-items: center;
			cursor: pointer;

			&__label {
				display: inline-block;
				transition: $speed-quarter;

				&.is-active {
					border-bottom: var(--border-width) solid var(--highlight);
				}
			}

			&__input {
				@include visually-hidden;

				&:focus ~ .el-switch__core {
					box-shadow:
						0 0 0 var(--input__border-width) var(--highlight),
						inset 0 0 0 1.5px var(--form__background-color);
				}
			}

			&__core {
				display: inline-block;
				position: relative;
				flex-shrink: 0;

				// Element has width as a prop, so !important is necessary to override.
				width: $switch--core-width !important;
				height: $switch--width + ($switch--padding * 2);
				background: hsla(var(--background-hsl), .8);
				border-radius: var(--toggle__border-radius, var(--input__border-radius));
				box-shadow: inset 1px 1px 1px rgb(black, 10%);
				margin-right: $gutter-third;
				margin-left: $gutter-third;
				transition: background-color $speed-half;

				&::after {
					position: absolute;
					left: 0;
					top: 0;
					width: $switch--width;
					height: $switch--width;
					background: white;
					border-radius: calc(var(--toggle__border-radius, var(--input__border-radius) - 2px));
					box-shadow: 1px 1px 1px 0 hsla(0deg, 0%, 0%, 20%), inset 0 0 2px hsla(0deg, 0%, 100%, 50%);
					margin: $switch--padding;
					content: '';
					transition: all $speed-half;

					@include popper-bg;
				}

				@media print {
					display: block; /* IE */
					outline: 1px solid var(--input__border-color);

					&::after {
						display: block; /* IE */
						outline: 1px solid var(--input__border-color);
					}
				}
			}
		}

		.is-checked .el-switch__core {
			background-color: var(--highlight);
			border-color: var(--highlight);

			&::after {
				left: $switch--width;
				box-shadow: none;
			}
		}

		&--native {
			display: flex;
			align-items: center;

			span {
				cursor: pointer;
			}

			input {
				margin-right: 4px;
				margin-left: 4px;
			}

			[type='range'] {
				width: 30px;
			}

			::-ms-track {
				color: transparent;
			}
		}

		&--condensed {

			.el-switch__label {
				display: none;
			}

			.el-switch__core {
				margin-right: 0;
				margin-left: 0;
			}
		}

		&__condensed-label {

			&::before {
				display: inline-block;
				width: .75em;
				height: .75em;
				border: 2px solid hsla(var(--background-hsl), 1);
				border-radius: 50%;
				margin-right: .2em;
				transform: scale(.8);
				content: '';
				transition: $speed-quarter;
			}

			&.is-active::before {
				background: var(--highlight);
				border: 2px solid transparent;
				box-shadow: 0 0 6px var(--highlight);
				transform: scale(.7);
			}
		}
	}
}
</style>
